(() => {
  'use strict';

  const RUNTIME_KEY = '__u20SecondaryEmbersV458385';
  const SECTION_SELECTOR = '[data-u20-secondary-surface]';
  const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PARTICLE_COUNT = 36;
  const RESOLUTION_SCALE = 0.6;
  const FRAME_INTERVAL = 1000 / 30;

  if (window[RUNTIME_KEY]) {
    window[RUNTIME_KEY].refresh();
    return;
  }

  let observer = null;
  let resizeObserver = null;
  let canvas = null;
  let context = null;
  let activeSection = null;
  let particles = [];
  let animationFrame = 0;
  let lastFrameTime = 0;
  const visibleAreas = new Map();
  const observedSections = new Set();

  const between = (min, max) => Math.random() * (max - min) + min;

  const canRun = () => DESKTOP_QUERY.matches && !REDUCED_MOTION.matches && !document.hidden;

  const resetParticle = (particle, randomizeY = false) => {
    const width = canvas?.width || 1;
    const height = canvas?.height || 1;
    particle.x = between(width * 0.06, width * 0.94);
    particle.y = randomizeY ? between(height * 0.18, height) : between(height * 0.9, height);
    particle.size = between(0.9, 2.6);
    particle.vx = between(-1.1, 1.1);
    particle.vy = between(-6.4, -3.1);
    particle.gravity = 0.002;
    particle.life = between(100, 260);
    particle.opacity = randomizeY ? between(0.18, 0.9) : 1;
    particle.fade = between(0.004, 0.008);
    particle.color = ['#e74c3c', '#c0392b', '#e67e22', '#f39c12'][Math.floor(Math.random() * 4)];
  };

  const resetParticles = () => {
    particles = Array.from({ length: PARTICLE_COUNT }, () => {
      const particle = {};
      resetParticle(particle, true);
      return particle;
    });
  };

  const sizeCanvas = () => {
    if (!canvas || !activeSection) return;
    const width = Math.max(1, Math.round(activeSection.clientWidth * RESOLUTION_SCALE));
    const height = Math.max(1, Math.round(activeSection.clientHeight * RESOLUTION_SCALE));
    if (canvas.width === width && canvas.height === height) return;
    canvas.width = width;
    canvas.height = height;
    resetParticles();
  };

  const draw = (time) => {
    if (!activeSection || !canvas || !context || !canRun()) {
      animationFrame = 0;
      return;
    }

    animationFrame = window.requestAnimationFrame(draw);
    if (time - lastFrameTime < FRAME_INTERVAL) return;
    lastFrameTime = time - ((time - lastFrameTime) % FRAME_INTERVAL);

    context.clearRect(0, 0, canvas.width, canvas.height);
    particles.forEach((particle) => {
      context.globalAlpha = particle.opacity;
      context.beginPath();
      context.fillStyle = particle.color;
      context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      context.fill();

      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.vy += particle.gravity;
      particle.opacity = Math.max(0, particle.opacity - particle.fade);
      particle.life -= 1;

      if (particle.life <= 0 || particle.opacity <= 0 || particle.y < -10) {
        resetParticle(particle);
      }
    });
    context.globalAlpha = 1;
  };

  const stopAnimation = () => {
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    lastFrameTime = 0;
    if (context && canvas) context.clearRect(0, 0, canvas.width, canvas.height);
  };

  const removeCanvas = () => {
    stopAnimation();
    resizeObserver?.disconnect();
    resizeObserver = null;
    canvas?.remove();
    canvas = null;
    context = null;
    particles = [];
    activeSection = null;
  };

  const attachCanvas = (section) => {
    if (!section || !canRun()) return removeCanvas();
    if (activeSection === section && canvas?.isConnected) {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(draw);
      return;
    }

    stopAnimation();
    activeSection = section;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.className = 'u20-secondary-embers-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      context = canvas.getContext('2d', { alpha: true });
    }
    section.prepend(canvas);
    sizeCanvas();

    resizeObserver?.disconnect();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(sizeCanvas);
      resizeObserver.observe(section);
    }

    lastFrameTime = 0;
    animationFrame = window.requestAnimationFrame(draw);
  };

  const selectActiveSection = () => {
    if (!canRun()) return removeCanvas();
    let nextSection = null;
    let largestArea = 0;
    visibleAreas.forEach((area, section) => {
      if (section.isConnected && area > largestArea) {
        largestArea = area;
        nextSection = section;
      }
    });
    if (largestArea > 0) attachCanvas(nextSection);
    else removeCanvas();
  };

  const createObserver = () => {
    if (observer || !('IntersectionObserver' in window) || !canRun()) return;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visibleAreas.set(
          entry.target,
          entry.isIntersecting ? entry.intersectionRect.width * entry.intersectionRect.height : 0
        );
      });
      selectActiveSection();
    }, { threshold: [0, 0.01, 0.25, 0.5, 0.75, 1] });
    observedSections.forEach((section) => observer.observe(section));
  };

  const destroyObserver = () => {
    observer?.disconnect();
    observer = null;
    visibleAreas.clear();
    removeCanvas();
  };

  const refresh = (scope = document) => {
    const sections = scope.matches?.(SECTION_SELECTOR)
      ? [scope, ...scope.querySelectorAll(SECTION_SELECTOR)]
      : [...scope.querySelectorAll(SECTION_SELECTOR)];
    sections.forEach((section) => {
      if (observedSections.has(section)) return;
      observedSections.add(section);
      observer?.observe(section);
    });
    if (canRun()) createObserver();
  };

  const handleModeChange = () => {
    if (canRun()) {
      createObserver();
      observedSections.forEach((section) => observer?.observe(section));
    } else {
      destroyObserver();
    }
  };

  const handleVisibilityChange = () => {
    if (document.hidden) stopAnimation();
    else handleModeChange();
  };

  const handleSectionUnload = (event) => {
    event.target.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      observer?.unobserve(section);
      observedSections.delete(section);
      visibleAreas.delete(section);
      if (activeSection === section) removeCanvas();
    });
    selectActiveSection();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  DESKTOP_QUERY.addEventListener?.('change', handleModeChange);
  REDUCED_MOTION.addEventListener?.('change', handleModeChange);

  refresh();
  window[RUNTIME_KEY] = { refresh };
})();
